/**
 * Number and currency formatting utilities for keyword review components.
 */

/**
 * Formats a number to a string with optional decimal places.
 * Only shows decimal places if the number has decimals.
 * @param value - The number to format
 * @param maxDecimals - Maximum number of decimal places (default: 2)
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted string
 */
export function formatNumber(
  value: number,
  maxDecimals = 2,
  options: Intl.NumberFormatOptions = {}
): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: 0,
    ...options
  }).format(value);
}

/**
 * Formats a number as a percentage
 * @param value - The number to format (0.1 = 10%)
 * @param maxDecimals - Maximum number of decimal places (default: 2)
 */
export function formatPercent(value: number, maxDecimals = 2): string {
  return formatNumber(value * 100, maxDecimals) + '%';
}

/**
 * Formats a number as currency using proper internationalization.
 * @param value - The number to format
 * @param maxDecimals - Maximum number of decimal places (default: 2)
 * @param currencyCode - ISO 4217 currency code (default: 'USD')
 */
export function formatCurrency(
  value: number,
  maxDecimals = 2,
  currencyCode = '$'
): string {
  return currencyCode + formatNumber(value, maxDecimals);
}
